'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';

const OrderLocationMapDynamic = dynamic(
  () =>
    import('./components/OrderLocationMapLeaflet').then((m) => ({
      default: m.OrderLocationMap,
    })),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center h-[350px] rounded-lg border border-gray-300 text-gray-400 text-sm">
        Loading map...
      </div>
    ),
  },
);

export interface OrderLocationModalProps extends BaseModalProps {
  latitude: number;
  longitude: number;
  locationLabel?: string;
}

function applyFuzzyOffset(coord: number): number {
  return coord + (Math.random() * 0.006 - 0.003);
}

export function OrderLocationModal({
  isOpen,
  onClose,
  latitude,
  longitude,
  locationLabel,
}: OrderLocationModalProps) {
  const { fuzzyLat, fuzzyLng } = useMemo(() => {
    if (!isOpen) return { fuzzyLat: latitude, fuzzyLng: longitude };
    return {
      fuzzyLat: applyFuzzyOffset(latitude),
      fuzzyLng: applyFuzzyOffset(longitude),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      animation="slide-up"
      title="Job Location"
      description={locationLabel ?? 'Approximate area where the work needs to be done'}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <svg
            className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-amber-800">
            Approximate location. Exact address is shared after order acceptance.
          </p>
        </div>

        <OrderLocationMapDynamic
          latitude={fuzzyLat}
          longitude={fuzzyLng}
          circleRadiusMeters={500}
          height="350px"
        />
      </div>
    </Modal>
  );
}
